<script setup lang="ts">
/**
 * ユーザ管理画面(Adminグループ専用)。
 * Cognito User Poolのユーザーを一覧表示し、キーワード・グループ・有効/無効状態で
 * 絞り込む。新規登録・編集・削除(無効化)を行う。
 *
 * これまでAWS CLIで手動実行していた下記2コマンドに相当する処理をWeb化したもの
 * (VB版デスクトップアプリには対応する画面が無い、新規機能)。
 *   aws cognito-idp admin-create-user --user-pool-id <Pool> --username <email> ...
 *   aws cognito-idp admin-add-user-to-group --user-pool-id <Pool> --username <email> --group-name <Group>
 *
 * アクセス制御は router (beforeEnter) と API側(is_admin)の二重で行っており、
 * このコンポーネント自体は「Adminとしてここに来ている」前提で作っている。
 */
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useResultModalStore } from '@/stores/resultModal'
import { useUsers, useSetUserStatusMutation } from '@/api/queries'
import type { UserSummary } from '@/types/api'
import UserFormModal from '@/components/UserFormModal.vue'

const router = useRouter()
const auth = useAuthStore()
const confirmModal = useConfirmModalStore()
const resultModal = useResultModalStore()

// ---- フィルタ ----
const keywordInput = ref('')
const keyword = ref('')
let debounceTimer: ReturnType<typeof setTimeout> | undefined
function onKeywordInput() {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => (keyword.value = keywordInput.value.trim()), 300)
}
const groupFilter = ref('')
const statusFilter = ref('')

const { data, isLoading, isError, refetch } = useUsers(keyword, groupFilter, statusFilter)

// ---- 新規登録/編集モーダル ----
const showFormModal = ref(false)
const editingUser = ref<UserSummary | null>(null)
function openCreate() {
  editingUser.value = null
  showFormModal.value = true
}
function openEdit(user: UserSummary) {
  editingUser.value = user
  showFormModal.value = true
}
function onFormSaved() {
  showFormModal.value = false
  refetch()
}

// ---- 無効化/有効化(削除の代替) ----
const statusMutation = useSetUserStatusMutation()
async function toggleStatus(user: UserSummary) {
  const willEnable = !user.enabled
  // ユーザープールは市民革命と共有しているため、無効化はアカウント全体(両アプリ)に効く
  const revoNote = user.alsoRevo
    ? (willEnable ? '市民革命にも再びログインできるようになります。' : 'このユーザーは市民革命も利用しています。市民革命にもログインできなくなります。')
    : ''
  const message = willEnable
    ? `${user.email} を有効化しますか？${revoNote}`
    : `${user.email} を無効化します。ログインできなくなりますが、後でいつでも有効化に戻せます。${revoNote}よろしいですか？`
  if (!(await confirmModal.ask(message))) return

  try {
    await statusMutation.mutateAsync({ username: user.username, req: { enabled: willEnable } })
    resultModal.show(willEnable ? 'ユーザーを有効化しました。' : 'ユーザーを無効化しました。')
  } catch (e) {
    resultModal.show((e as Error).message, 'ng')
  }
}

function groupLabel(group: string) {
  return group === 'Admin' ? '管理者' : '一般'
}
</script>

<template>
  <div class="um-shell">
    <div class="um-topbar">
      <div class="um-brandmark" title="トップ画面に戻る" @click="router.push('/')">
        <div class="um-logo">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div>ユーザ管理<div class="um-sub">SalMon User Management</div></div>
      </div>
      <div class="topbar-spacer"></div>
      <div class="op-chip" v-if="auth.isAuthenticated">
        <span class="dot"></span> {{ auth.displayName }}
      </div>
      <button class="btn btn-icon topbar-icon-btn" title="ログアウト" @click="auth.logout()">
        <svg class="icon" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      </button>
    </div>

    <div class="um-body">
      <div class="um-toolbar">
        <input
          v-model="keywordInput"
          @input="onKeywordInput"
          class="edit-input um-search"
          type="text"
          placeholder="メールアドレス・表示名で検索"
        />
        <select v-model="groupFilter" class="edit-input um-select">
          <option value="">すべてのグループ</option>
          <option value="Admin">管理者(Admin)</option>
          <option value="General">一般(General)</option>
        </select>
        <select v-model="statusFilter" class="edit-input um-select">
          <option value="">すべての状態</option>
          <option value="enabled">有効のみ</option>
          <option value="disabled">無効のみ</option>
        </select>
        <div class="um-toolbar-spacer"></div>
        <button class="btn btn-primary" @click="openCreate">
          <svg class="icon icon-xs" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          新規登録
        </button>
      </div>

      <div v-if="isLoading" class="um-state">読み込み中…</div>
      <div v-else-if="isError" class="um-state um-state--error">
        ユーザー一覧の取得に失敗しました。
        <button class="btn btn-ghost btn-sm" @click="refetch()">再試行</button>
      </div>
      <div v-else-if="!data?.items.length" class="um-state">該当するユーザーがいません。</div>

      <table v-else class="um-table">
        <thead>
          <tr>
            <th>メールアドレス</th>
            <th>表示名</th>
            <th>グループ</th>
            <th>状態</th>
            <th>作成日</th>
            <th class="um-col-ops">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in data.items" :key="u.username" :class="{ 'um-row--disabled': !u.enabled }">
            <td>{{ u.email }}</td>
            <td>{{ u.preferredUsername || '-' }}</td>
            <td>
              <span class="um-badge" :class="u.group === 'Admin' ? 'um-badge--admin' : 'um-badge--general'">
                {{ groupLabel(u.group) }}
              </span>
              <span v-if="u.alsoRevo" class="um-badge um-badge--revo" title="市民革命の権限も持っています(アカウント共通)">市民革命</span>
            </td>
            <td>
              <span class="um-badge" :class="u.enabled ? 'um-badge--enabled' : 'um-badge--disabled'">
                {{ u.enabled ? '有効' : '無効' }}
              </span>
            </td>
            <td class="um-col-date">{{ u.createDate ? u.createDate.slice(0, 10) : '-' }}</td>
            <td class="um-col-ops">
              <button class="btn btn-ghost btn-sm" @click="openEdit(u)">編集</button>
              <button
                class="btn btn-ghost btn-sm"
                :class="{ 'btn-danger': u.enabled }"
                @click="toggleStatus(u)"
              >
                {{ u.enabled ? '削除(無効化)' : '有効化' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <UserFormModal
      v-if="showFormModal"
      :user="editingUser"
      @close="showFormModal = false"
      @saved="onFormSaved"
    />
  </div>
</template>

<style scoped>
.um-shell{min-height:100vh;display:flex;flex-direction:column;background:var(--bg);}

.um-topbar{
  display:flex;align-items:center;gap:14px;padding:0 20px;height:58px;flex:none;
  background:var(--panel);border-bottom:1px solid var(--line);box-shadow:var(--shadow-sm);
}
.um-brandmark{display:flex;align-items:center;gap:10px;font-weight:700;font-size:17px;color:#8A4F16;cursor:pointer;}
.um-logo{
  width:30px;height:30px;border-radius:9px;display:flex;align-items:center;justify-content:center;
  background:linear-gradient(135deg,#E5A94F,#B0691F);
}
.um-logo svg{width:16px;height:16px;stroke:#fff;stroke-width:2;}
.um-sub{font-weight:400;font-size:11px;color:#B98545;letter-spacing:.6px;text-transform:uppercase;}

.um-body{flex:1;padding:24px 28px;display:flex;flex-direction:column;gap:16px;max-width:1100px;width:100%;margin:0 auto;}

.um-toolbar{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.um-search{width:280px;}
.um-select{width:180px;}
.um-toolbar-spacer{flex:1;}

.um-state{
  padding:40px 0;text-align:center;color:var(--ink-soft);font-size:13px;
  display:flex;flex-direction:column;align-items:center;gap:10px;
}
.um-state--error{color:var(--ng);}

.um-table{
  width:100%;border-collapse:collapse;background:var(--panel);border:1px solid var(--line);
  border-radius:12px;overflow:hidden;box-shadow:var(--shadow-sm);
}
.um-table th{
  text-align:left;font-size:11.5px;font-weight:700;color:var(--ink-soft);
  padding:10px 14px;background:var(--panel-alt);border-bottom:1px solid var(--line);
}
.um-table td{padding:10px 14px;font-size:13px;color:var(--ink);border-bottom:1px solid var(--line);}
.um-table tr:last-child td{border-bottom:none;}
.um-row--disabled td{color:var(--ink-faint);}

.um-col-ops{white-space:nowrap;display:flex;gap:6px;}
.um-col-date{color:var(--ink-faint);font-size:12px;}

.um-badge{
  display:inline-block;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:700;
}
.um-badge--admin{background:#FCEAD3;color:#8A4F16;}
.um-badge--general{background:var(--panel-alt);color:var(--ink-soft);}
.um-badge--revo{background:#ECEEFD;color:#4B51C9;margin-left:4px;}
.um-badge--enabled{background:var(--ok-tint);color:var(--ok);}
.um-badge--disabled{background:var(--ng-tint);color:var(--ng);}
</style>
